/* eslint-disable arrow-body-style */
import React, { useState, useContext, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Button,
} from '@material-ui/core';

import CloseIcon from '@material-ui/icons/Close';

import { JobContext } from 'context/jobContextContainer';
import { NotificationContext } from 'components/NotificationOverlay';
import { jobsService } from 'services/jobsService';
import { DialogTitleContainer, RoutePlanContentContainer } from '../style';
import JobStep from './JobStep';
import VehicleStep from './VehicleStep';
import CalculationStep from './CalculationStep';
import { CircularProgressContext } from 'components/CircularProgressOverlay';
const steps = ['Job Selection', 'Vehicle Selection', 'Route Planning'];

const RoutePlanModal = ({ refreshReservationTable }) => {
  const { routePlanModalOpen, setRoutePlanModalOpen, routePlanData, driverList } = useContext(JobContext);
  const { showNotification } = useContext(NotificationContext);
  const [activeStep, setActiveStep] = useState(0);
  const [validationError, setValidationError] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingFinishData, setPendingFinishData] = useState(null);
  const { showCircularProgress, hideCircularProgress } = useContext(CircularProgressContext);
  const jobStepRef = useRef(null);
  const vehicleStepRef = useRef(null);
  const calculationStepRef = useRef(null);

  const handleEditClose = () => {
    setRoutePlanModalOpen(false);
    setActiveStep(0);
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (activeStep === 0 && jobStepRef.current) {
      const isValid = jobStepRef.current.validate();
      if (!isValid) {
        return;
      }
    }
    if (activeStep === 1 && vehicleStepRef.current) {
      const isValid = vehicleStepRef.current.validate();
      if (!isValid) {
        return;
      }
    }
    // Add validation for other steps here as needed
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleFinish = () => {
    if (calculationStepRef.current) {
      // Validate first
      const validation = calculationStepRef.current.validate();
      if (!validation.isValid) {
        setValidationError(validation.message);
        return;
      }

      // Get finish data and show confirmation dialog
      const finishData = calculationStepRef.current.getFinishData();
      setPendingFinishData(finishData);
      setConfirmDialogOpen(true);
    }
  };

  const handleConfirmAssign = async () => {
    if (!pendingFinishData) return;

    try {
      showCircularProgress('Loading');
      const res = await jobsService.bulk_assign_driver_to_job(pendingFinishData);
      if (res && res?.data?.status === 'SUCCESS') {
        showNotification('Successfully assigned drivers to jobs', 'success');
        refreshReservationTable();
        handleEditClose();
      } else if (res && res?.data?.status === 'FAIL') {
        showNotification(`Sorry, Failed to assign drivers: ${res.data?.data?.msg}`, 'error');
      }
    } catch (error) {
      console.error('Error assigning drivers:', error);
      showNotification('An error occurred while assigning drivers', 'error');
    } finally {
      hideCircularProgress();
      setConfirmDialogOpen(false);
      setPendingFinishData(null);
    }
  };

  const handleCancelConfirm = () => {
    setConfirmDialogOpen(false);
    setPendingFinishData(null);
  };

  const getDriverName = (driverId) => {
    if (!driverId || !driverList) return 'N/A';
    const driver = driverList.find(d => d.id === driverId);
    return driver ? driver.label : 'N/A';
  };

  return (
    <Dialog
      classes={{ paper: 'modal-content' }}
      fullWidth
      maxWidth="lg"
      open={routePlanModalOpen}
      onClose={handleEditClose}
      disableBackdropClick
      disableEscapeKeyDown
    >
      <DialogTitleContainer className="bg-secondary">
        <span>Route Plan</span>
        <IconButton
          aria-label="close"
          onClick={handleEditClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitleContainer>
      <RoutePlanContentContainer className="p-3" dividers>
        <Stepper activeStep={activeStep} style={{ marginBottom: '20px' }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div className="dialog-content--wrapper bg-secondary border-0" style={{ height: '550px' }}>
          <div style={{ display: activeStep === 0 ? 'block' : 'none' }}>
            <JobStep ref={jobStepRef} />
          </div>
          <div style={{ display: activeStep === 1 ? 'block' : 'none' }}>
            <VehicleStep ref={vehicleStepRef} isActive={activeStep === 1} />
          </div>
          <div style={{ display: activeStep === 2 ? 'block' : 'none', height: '100%' }}>
            <CalculationStep ref={calculationStepRef} isActive={activeStep === 2} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px' }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={activeStep === steps.length - 1 ? handleFinish : handleNext}
          >
            {activeStep === steps.length - 1 ? 'Finish' : 'Next'}
          </Button>
        </div>
      </RoutePlanContentContainer>

      <Dialog
        open={validationError !== null}
        onClose={() => setValidationError(null)}
        aria-labelledby="validation-dialog-title"
      >
        <DialogTitle id="validation-dialog-title">Validation Error</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {validationError}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationError(null)} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelConfirm}
        aria-labelledby="confirm-dialog-title"
        maxWidth="md"
        fullWidth
      >
        <DialogTitle id="confirm-dialog-title">Confirm Driver Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '20px' }}>
            Are you sure you want to assign these jobs to these drivers?
          </DialogContentText>
          {pendingFinishData && pendingFinishData.length > 0 && (
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {pendingFinishData.map((item, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: '15px',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    background: '#f9f9f9',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                    Vehicle ID: {item.vehicle_id}
                  </div>
                  <div style={{ marginBottom: '4px' }}>
                    <strong>Driver:</strong> {getDriverName(item.driver_id)}
                  </div>
                  <div>
                    <strong>Jobs ({item.job_ids.length}):</strong>{' '}
                    {item.job_ids.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAssign} color="primary" variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default RoutePlanModal;
